/* @flow */

import * as React from 'react';
import { every, get, isEmpty, noop, some } from 'lodash/fp';
import styled from '@emotion/styled';

import t from 'app/utils/translate';
import PlayerPositionTab from './PlayerPositionTab';

const allFilled = every('filled');
const containsError = some('hasError');

type Props = {
  autoAdvancePositions?: boolean,
  onSelect?: (string | null) => void,
  positions: Array<Object>,
  requiredSelections?: number,
  selectedPosition?: string,
  showAllTab?: boolean,
  showSelectionFeedback?: boolean,
  totalSelected?: number
};

const PlayerPositionsNav = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  overflow: 'scroll',
  position: 'relative',
  WebkitOverflowScrolling: 'touch',
  [theme.mediaQuery.desktopAndUp]: {
    overflow: 'hidden'
  }
}));

class PlayerPositionsBar extends React.PureComponent<Props> {
  props: Props;

  static defaultProps = {
    autoAdvancePositions: false,
    onSelect: noop,
    requiredSelections: 0,
    selectedPosition: undefined,
    showAllTab: true,
    showSelectionFeedback: false,
    totalSelected: 0
  };

  componentDidUpdate(prevProps: Props) {
    const { autoAdvancePositions, onSelect, positions, selectedPosition: positionId } = this.props;
    const { positions: prevPositions, selectedPosition: prevPositionId } = prevProps;

    if (
      !autoAdvancePositions ||
      !positionId ||
      !Array.isArray(positions) ||
      !Array.isArray(prevPositions) ||
      positions.length <= 1 ||
      positionId !== prevPositionId
    ) {
      return;
    }

    const selectedPosition = positions.find(pos => pos.id === positionId);
    const prevSelectedPosition = prevPositions.find(pos => pos.id === positionId);

    if (
      !isEmpty(selectedPosition) &&
      !isEmpty(prevSelectedPosition) &&
      selectedPosition.filled &&
      !prevSelectedPosition.filled
    ) {
      const nextPosition = positions.find(pos => pos.id !== selectedPosition.id && !pos.filled);
      if (nextPosition) {
        onSelect(nextPosition.id);
      }
    }
  }

  render() {
    const {
      onSelect,
      positions,
      requiredSelections,
      selectedPosition,
      showAllTab,
      showSelectionFeedback,
      totalSelected
    } = this.props;

    return (
      <PlayerPositionsNav>
        {showAllTab && (
          <PlayerPositionTab
            enabled={!selectedPosition}
            filled={requiredSelections === totalSelected && allFilled(positions)}
            hasError={containsError(positions)}
            label={t('All')}
            max={requiredSelections}
            min={requiredSelections}
            onSelect={() => onSelect(null)}
            selected={totalSelected}
            showSelectionFeedback={showSelectionFeedback}
          />
        )}
        {positions.map(pos => {
          if (pos.shareQuota && pos.isTeam) {
            return null;
          }
          let { selected } = pos;
          if (pos.shareQuota && !pos.isTeam) {
            const teamPos = positions.find(p => p.id === pos.id && p.isTeam);
            selected += get('selected', teamPos) || 0;
          }
          return (
            <PlayerPositionTab
              key={pos.id}
              enabled={pos.id === selectedPosition}
              filled={pos.filled}
              hasError={pos.hasError}
              label={pos.abbr}
              max={pos.max}
              min={pos.min}
              onSelect={() => onSelect(pos.id)}
              selected={selected}
              showSelectionFeedback={showSelectionFeedback}
            />
          );
        })}
      </PlayerPositionsNav>
    );
  }
}

export default PlayerPositionsBar;
